"use client";

import { useState } from "react";
import * as Yup from "yup";
import { FiCheckCircle } from "react-icons/fi";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import TextInput from "@components/ui/form/text-input";
import Textarea from "@components/ui/form/textarea";
import { FaClock, FaMapPin, FaPhone } from "react-icons/fa";
import { MdAlternateEmail } from "react-icons/md";
import Button from "@components/ui/form/button";
import { facilities, type Facility } from "@/data/facilities";

const defaultFacility = facilities.find((f) => f.status === "active") ?? facilities[0];

const validationSchema = Yup.object().shape({
	name: Yup.string().max(50, "För långt namn").required("Fyll i ditt namn"),
	email: Yup.string().email("Ogiltig e-postadress").required("Fyll i din e-postadress"),
	phone: Yup.string().required("Fyll i ditt telefonnummer"),
	message: Yup.string().max(1000, "För långt meddelande").required("Fyll i meddelande"),
});

const ContactForm = ({ facility = defaultFacility }: { facility?: Facility }) => {
	const {
		handleSubmit,
		formState: { errors, isSubmitting },
		register,
	} = useForm<any>({
		defaultValues: {
			name: "",
			email: "",
			phone: "",
			message: "",
		},
		resolver: yupResolver(validationSchema),
	});

	const [isSent, setIsSent] = useState(false);

	const onSubmit = async (values) => {
		await fetch("/api/contact", {
			method: "POST",
			body: JSON.stringify({ ...values, facility: facility?.slug }),
			headers: {
				"Content-Type": "application/json",
			},
		}).then(() => {
			setIsSent(true);
		});
	};
	return (
		<div className="max-w-5xl grid grid-cols-1 lg:grid-cols-5 mx-auto text-left rounded-xl border border-white/[0.06] overflow-hidden">
			<div className="col-span-3 py-8 px-6 bg-white/[0.03]">
				{!isSent ? (
					<form onSubmit={handleSubmit(onSubmit)}>
						<div className="flex flex-col gap-4">
							<div className="space-y-2">
								<div className="grid col-span-1 sm:grid-cols-2 sm:gap-4">
									<TextInput label="Ditt namn" type="text" error={errors?.name} withAsterisk {...register("name")} />
									<TextInput
										label="Din e-postadress"
										type="email"
										placeholder="@"
										error={errors?.email}
										withAsterisk
										{...register("email")}
									/>
								</div>
								<div className="grid col-span-1 gap-4">
									<TextInput
										label="Telefonnummer"
										type="tel"
										error={errors?.phone}
										withAsterisk
										{...register("phone")}
									/>
								</div>
								<div className="grid col-span-1 gap-4">
									<Textarea label="Meddelande*" error={errors?.message} withAsterisk={false} {...register("message")} />
								</div>
							</div>
						</div>

						<Button
							className="mt-8"
							variant="accent"
							full
							loading={isSubmitting}
							type="submit"
							disabled={isSubmitting || isSent}
						>
							Skicka
						</Button>
					</form>
				) : (
					<div className="flex items-center gap-3 p-5 rounded-lg bg-success/10 border border-success/20">
						<FiCheckCircle size={20} className="text-success shrink-0" />
						<div>
							<p className="font-semibold text-white text-sm">Din förfrågan har skickats!</p>
							<p className="text-xs text-gray-400 mt-0.5">Vi återkommer inom kort.</p>
						</div>
					</div>
				)}
			</div>
			<ContactSidebar facility={facility} />
		</div>
	);
};

export const ContactSidebar = ({ facility = defaultFacility }: { facility?: Facility }) => {
	const f = facility ?? defaultFacility;
	return (
		<div className="col-span-2 p-8 bg-white/[0.06] border-l border-white/[0.06]">
			<div className="sticky top-28">
				<p className="text-sm text-gray-500 uppercase tracking-wide mb-6">Kontaktuppgifter — {f.name}</p>
				<div className="space-y-6">
					<div className="flex gap-4 items-center">
						<div className="rounded-lg bg-brand/10 p-3 flex items-center shrink-0">
							<FaMapPin className="text-brand" size={16} />
						</div>
						<div>
							<p className="text-xs text-gray-500 mb-0.5">Adress</p>
							<p className="text-sm text-white font-medium">
								{f.address}, {f.zipCity}
							</p>
						</div>
					</div>
					<div className="flex gap-4 items-center">
						<div className="rounded-lg bg-brand/10 p-3 flex items-center shrink-0">
							<FaPhone className="text-brand" size={16} />
						</div>
						<div>
							<p className="text-xs text-gray-500 mb-0.5">Telefon</p>
							<a
								href={`tel:${f.phone.replace(/[^0-9+]/g, "")}`}
								className="text-sm text-white font-medium hover:text-brand transition-colors"
							>
								{f.phone}
							</a>
						</div>
					</div>
					<div className="flex gap-4 items-center">
						<div className="rounded-lg bg-brand/10 p-3 flex items-center shrink-0">
							<MdAlternateEmail className="text-brand" size={16} />
						</div>
						<div>
							<p className="text-xs text-gray-500 mb-0.5">Email</p>
							<a
								href={`mailto:${f.email}`}
								className="text-sm text-white font-medium hover:text-brand transition-colors"
							>
								{f.email}
							</a>
						</div>
					</div>
					<div className="flex gap-4 items-start">
						<div className="rounded-lg bg-brand/10 p-3 flex items-center shrink-0">
							<FaClock className="text-brand" size={16} />
						</div>
						<div>
							<p className="text-xs text-gray-500 mb-0.5">Öppettider</p>
							<div className="text-sm text-white font-medium space-y-0.5">
								{f.openingHours.map((oh) => (
									<p key={oh.label}>
										{oh.label}: {oh.hours}
									</p>
								))}
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default ContactForm;
